import React, { Component, ReactNode } from 'react';
import './SignupPassword.scss';
import { InputAdornment } from '@material-ui/core';
import Input from '@material-ui/core/Input';
import IconButton from '@material-ui/core/IconButton';
import { get } from '../../../services/BackendService';
import { NotificationBanner } from '../../shared/NotificationBanner/NotificationBanner';
import { PasswordStrengthMeter } from '../../shared/PasswordStrengthMeter/PasswordStrengthMeter';
import { AUTH_API_URL, config } from '../../../app.config';

const debounce = require('lodash.debounce');

const ERROR_MESSAGES: { [key: string]: string } = {
  authenticationError: 'ERROR: Current password incorrect',
  passwordStrengthError: 'ERROR: Password is too weak. Please make it harder to guess.',
  passwordMatchError: "Your passwords don't match!",
};

export class SignupPassword extends Component<IPasswordCreateProps, IPasswordCreateState> {
  state = {
    password: '',
    passwordConfirm: '',
    passwordError: false,
    passwordConfirmed: false,
    validPassword: false,
    errorType: '',
    passwordStrength: { guesses: 0, strength: 0, isStrong: false },
    showPassword: false,
    showConfirmedPassword: false,
  };

  validatePasswordDebounce = debounce((p: string) => this.validatePassword(p), 400);
  passwordMatchDebounce = debounce((passwordConfirm: string) => this.checkIfPasswordsMatch(passwordConfirm), 300);

  async validatePassword(password: string) {
    const mHeaders = new Headers();
    mHeaders.append('password', password);
    const args: RequestInit = { method: 'get', headers: mHeaders };

    try {
      const res = await get<IPasswordStrength>(`${AUTH_API_URL}/api/validate-password`, args);
      if (res.parsedBody) {
        this.setState({ passwordStrength: res.parsedBody });
        this.setState({
          validPassword: this.passwordIsValid(this.state.password, this.state.passwordConfirm),
        });
      }
    } catch (err) {
      console.log('Password validation error', err);
    }
  }

  passwordIsValid(password: string, newPassword: string): boolean {
    if (password === newPassword) {
      if (!this.state.passwordStrength.isStrong) {
        return false;
      }
      this.setState({ errorType: '', passwordError: false });

      return true;
    } else {
      if (newPassword.length > 5) {
        this.setState({ errorType: 'passwordMatchError', passwordError: true });
      }

      return false;
    }
  }

  errorText(): string {
    return ERROR_MESSAGES[this.state.errorType] || '';
  }

  onPasswordChange(password: string) {
    this.setState({ password: password });
    this.validatePasswordDebounce(password);
  }

  onNextClicked = () => {
    if (this.passwordIsValid(this.state.password, this.state.passwordConfirm)) {
      this.props.passwordNext(this.state.password);
    }
  };

  onPasswordConfirmationChange = (password: string) => {
    this.passwordMatchDebounce(password);
  };

  checkIfPasswordsMatch = (passwordConfirm: string) => {
    this.setState({
      passwordConfirm: passwordConfirm,
      validPassword: this.passwordIsValid(this.state.password, passwordConfirm),
    });
  };

  handleClickShowPassword = () => {
    const previousValue = this.state.showPassword;
    this.setState({
      showPassword: !previousValue,
    });
  };

  handleClickShowConfirmedPassword = () => {
    const previousValue = this.state.showConfirmedPassword;
    this.setState({
      showConfirmedPassword: !previousValue,
    });
  };

  render(): ReactNode {
    return (
      <div className="hatters-flex-column-container">
        {this.state.passwordError && <NotificationBanner type={'error'} message={this.errorText()} />}

        <span className={'flex-spacer-small'} />

        <div className="password-wrapper">
          <div className="password-title-container">
            <div className="hatters-title">Create Password</div>
          </div>

          <div className="hatters-description-normal">
            Your data is precious - keep it safe with a password that is Strong or Very Strong.
          </div>

          <form
            className={'password-signup-form'}
            onSubmit={(e) => {
              e.preventDefault();
            }}
          >
            <input name={'username'} autoComplete={'username'} type={'text'} hidden={true} />
            <div className={'password-input-container'}>
              <label className={'input-label'} htmlFor={'password-1'}>
                Password
              </label>
              <Input
                className="hatters-input password-input"
                name={'password'}
                id={'password-1'}
                type={this.state.showPassword ? 'text' : 'password'}
                style={this.state.passwordError ? { borderColor: '#E45151' } : {}}
                placeholder="Type password here"
                autoComplete={'new-password'}
                onChange={(e) => this.onPasswordChange(e.target.value)}
                disableUnderline={true}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="Toggle password visibility"
                      tabIndex={-1}
                      onClick={this.handleClickShowPassword}
                    >
                      {this.state.showPassword ? (
                        <i className="material-icons">visibility</i>
                      ) : (
                        <i className="material-icons">visibility_off</i>
                      )}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </div>

            <label className={'input-label'} htmlFor={'password-2'}>
              Confirm Password
            </label>
            <Input
              className="hatters-input password-input"
              name={'password-2'}
              id={'password-2'}
              type={this.state.showConfirmedPassword ? 'text' : 'password'}
              style={this.state.passwordError ? { borderColor: '#E45151' } : {}}
              placeholder="Confirm password here"
              autoComplete={'new-password'}
              onChange={(e) => this.onPasswordConfirmationChange(e.target.value)}
              disableUnderline={true}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="Toggle password visibility"
                    tabIndex={-1}
                    onClick={this.handleClickShowConfirmedPassword}
                  >
                    {this.state.showConfirmedPassword ? (
                      <i className="material-icons">visibility</i>
                    ) : (
                      <i className="material-icons">visibility_off</i>
                    )}
                  </IconButton>
                </InputAdornment>
              }
            />
          </form>

          {this.state.password.length > 0 && (
            <>
              <PasswordStrengthMeter passwordStrength={this.state.passwordStrength} />
            </>
          )}

          <div className="hatters-description-small password-strength-policy">banana-papaya-malaga</div>
          <div className="hatters-description-small">
            <strong>Did you know?</strong>
            <br />
            This combination of three random words is one of the strongest passwords you can have. Read on how we
            determine password strength policy{' '}
            <a href={config.links.bestPractices} target="_blank" rel="noopener noreferrer" tabIndex={-1}>
              here
            </a>
          </div>

          <div className="password-user-actions">
            <button
              className="hatters-btn hatters-btn-accent"
              disabled={!this.state.validPassword}
              onClick={() => this.onNextClicked()}
            >
              Next
            </button>
            <button className="hatters-btn hatters-btn-transparent" onClick={() => this.props.cancelClicked()}>
              Cancel
            </button>
          </div>
        </div>

        <span className={'flex-spacer-large'} />
      </div>
    );
  }
}

interface IPasswordCreateState {
  password: string;
  passwordConfirm: string;
  passwordConfirmed: boolean;
  passwordError: boolean;
  validPassword: boolean;
  errorType: string;
  passwordStrength: IPasswordStrength;
  showPassword: boolean;
  showConfirmedPassword: boolean;
}

interface IPasswordCreateProps {
  passwordNext: Function;
  cancelClicked: Function;
}

export interface IPasswordStrength {
  isStrong: boolean;
  guesses: number;
  strength: number;
  suggestions?: string[];
}
